import {
    Breadcrumb,
    Button,
    Card,
    Col,
    Collapse,
    Divider,
    Form,
    message,
    Modal,
    Row,
    Select,
    Space,
    Tag,
    Tooltip
} from "antd";
import {useCallback, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {buildSteps, extractLocalizedValue, getStatusColor} from "../../../common";
import {VerificationStatus, VerificationStep} from "../../../common/constant"
import {
    useAbandonUserVerificationProfile,
    useAssignUserVerificationProfile,
    useInitializeUserVerificationHistory,
    useSaveUserVerificationHistory,
    useSubmitUserVerificationHistory,
    useUpdateUserVerificationHistoryStatus
} from "../../../function/accountVerification";
import {debounce} from "lodash";
import {globalColorThemeState, globalNotificationState} from "../../../../../_common/global_state";
import {useRecoilValue, useSetRecoilState} from "recoil";
import {convertToUppercase, useIsMobile} from "../../../../../_common/function";
import {initializeDocumentForm, renderFormFields} from "../../../common/form";
import {verificationRefetchState} from "../../../state";
import {CaretDownFilled, FireOutlined} from "@ant-design/icons";
import {countryList_language} from "../../../../../../config/countryList_language";
import {isMobile} from "react-device-detect";
import CollapsePanel from "antd/es/collapse/CollapsePanel";
import {
    apiCancelVerificationHistory,
    apiDuplicateUserVerificationHistory
} from "../../../../../api/restful/verification";
import {apiGetQuestionnaireRecordStatus} from "../../../../../api/restful/questionnaire";
import {cleanStep} from "../../../common";

export default function AccountVerify_2(props) {
    const {
        userVerificationFlows,
        getUserVerificationFlows,
        userVerificationProfile,
        refetchUserVerificationProfile,
        userVerificationTiers,
        refetchUserVerificationTiers,
        userVerificationHistories,
        refetchUserVerificationHistories,
        accessRightsList
    } = props

    const {t} = useTranslation();

    const initialStep = userVerificationProfile?.flowSetting ? VerificationStep.Tier : VerificationStep.Flow;

    const {
        updateUserVerificationHistoryStatus,
        loading: updateHistoryStatusLoading
    } = useUpdateUserVerificationHistoryStatus();

    const [currentStep, setCurrentStep] = useState(initialStep);
    const [selectedVerificationType, setSelectedVerificationType] = useState(null);
    const selectedFlow = userVerificationFlows?.find(flow => flow._id === userVerificationProfile?.flowSetting) || null;
    const [selectedTier, setSelectedTier] = useState(null);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [selectedInnerDocument, setSelectedInnerDocument] = useState(null);
    const [selectedCountry, setSelectedCountry] = useState(userVerificationProfile?.country)
    const [cancelOpen, setCancelOpen] = useState(false);

    const currentHistory = userVerificationHistories?.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))?.find((history) => history.tierSetting === selectedTier?._id)
    const nextTierAvailableStatus = ["pending_verification", "verified"].includes(userVerificationHistories?.[0]?.status)
    const tierLevelArray = userVerificationTiers?.map(d => d?.tierLevel)
    const tierSettingNextTierExist = userVerificationHistories?.[0]?.tierLevel < Math.max(...tierLevelArray)

    const goNext = () => setCurrentStep(prev => prev + 1);
    const goTo = (step) => setCurrentStep(step);
    const goBack = () => setCurrentStep(prev => prev - 1);

    const handleSelectVerificationType = (type) => {
        setSelectedVerificationType(type);
    }

    const handleStartVerification = async () => {
        await Promise.all([
            refetchUserVerificationTiers(),
            refetchUserVerificationProfile(),
        ]);
    }

    const handleResubmitVerification = async () => {

        if (!currentHistory?._id || currentHistory.status !== VerificationStatus.PENDING) return;

        const payload = {
            _id: currentHistory._id,
            status: VerificationStatus.DRAFT
        }

        await updateUserVerificationHistoryStatus(payload)
        await Promise.all([
            refetchUserVerificationHistories(),
        ])
    }

    const buildBreadcrumbItems = () => {
        const items = [];

        if (selectedFlow) {
            items.push({
                title: extractLocalizedValue(selectedFlow?.displayName) || selectedFlow?.name,
            });
        }

        if (currentStep > VerificationStep.Tier && selectedTier) {

            if (currentStep === VerificationStep.Document) {
                const tierRequired = selectedTier?.numberOfRequired;
                const documentsRequired = selectedTier?.documentSettings?.filter(doc => doc?.isRequired)?.length

                let title = extractLocalizedValue(selectedTier?.displayName) || selectedTier?.name
                let message = ""

                if (tierRequired === selectedTier?.documentSettings?.length || tierRequired === documentsRequired) {
                    message = t("accountVerification.pleaseCompleteAllSections");
                } else if (tierRequired > 0) {
                    message = t("accountVerification.pleaseCompleteAnySections", {numberOfRequired: tierRequired});
                } else {
                    message = ""
                }

                if (message) {
                    title = title.concat(` (${message})`)
                }

                items.push({title});
            } else {
                items.push({title: extractLocalizedValue(selectedTier?.displayName) || selectedTier?.name,});
            }
        }

        if (currentStep > VerificationStep.Document && selectedDocument) {

            if (currentStep === VerificationStep.DocumentGroup) {
                const documentRequired = selectedDocument?.numberOfRequired;
                const innerDocumentsRequired = selectedDocument?.steps?.filter(doc => doc?.isRequired)?.length

                let title = extractLocalizedValue(selectedDocument?.displayName) || selectedDocument?.name
                let message = ""

                if (documentRequired === selectedDocument?.steps?.length || documentRequired === innerDocumentsRequired) {
                    message = t("accountVerification.pleaseCompleteAllSections");
                } else if (documentRequired > 0) {
                    message = t("accountVerification.pleaseCompleteAnySections", {numberOfRequired: documentRequired});
                } else {
                    message = ""
                }

                if (message) {
                    title = title.concat(` (${message})`)
                }

                items.push({title});
            } else if (selectedInnerDocument) {
                items.push({title: extractLocalizedValue(selectedDocument?.displayName) || selectedDocument?.name,});
            }
        }

        if (currentStep > VerificationStep.DocumentGroup && selectedDocument) {
            let title = extractLocalizedValue(selectedInnerDocument?.displayName || selectedDocument?.displayName) || selectedInnerDocument?.name || selectedDocument?.name;

            const verificationStatus = currentHistory?.status;

            items.push({
                title: (
                    <span>
                        {title}
                        {verificationStatus !== VerificationStatus.DRAFT && (
                            <Tag
                                color={getStatusColor(verificationStatus)}
                                style={{marginLeft: 6}}
                            >
                                {convertToUppercase(verificationStatus.replace(/_/g, " "))}
                            </Tag>
                        )}
                    </span>
                ),
            });
        }

        return items;
    }

    useEffect(() => {
        const statusList = userVerificationHistories?.map(d => d?.status)
        const latestRecord = userVerificationHistories?.[0]

        if (nextTierAvailableStatus && tierSettingNextTierExist) {
            setCurrentStep(VerificationStep.Tier);
        } else if (statusList?.includes("pending_verification")) {
            setCurrentStep(VerificationStep.Status);
        } else if (userVerificationProfile?.tierSetting && userVerificationProfile?.status === "draft_verification") {
            setSelectedTier(userVerificationTiers?.find(d => d?._id === userVerificationProfile?.tierSetting))
            setCurrentStep(VerificationStep.Document)
        } else if (userVerificationProfile?.flowSetting) {
            setCurrentStep(VerificationStep.Tier);
        } else if (userVerificationProfile?.status === "not_verified") {
            setCurrentStep(VerificationStep.Country)
        } else {
            setCurrentStep(VerificationStep.Flow);
        }
    }, [userVerificationProfile, userVerificationTiers])

    const breadcrumbItems = buildBreadcrumbItems(
        currentStep,
        selectedFlow,
        selectedTier,
        selectedDocument,
        extractLocalizedValue
    );

    const handleCancelFlow = () => {
        setCancelOpen(true)
    }

    return (
        <div id={"accountVerify"}>
            <Row justify={"center"} style={{marginBottom: "60px"}}>
                <div className={"w-full lg:mx-12"}>
                    <Space style={{width: "100%"}} size={24} direction="vertical" className={"w-full"}>

                        {
                            currentStep > VerificationStep.Flow && currentStep < VerificationStep.Status && (
                                <div className="breadcrumb-header">
                                    <div className="breadcrumb-container">
                                        <Breadcrumb
                                            items={breadcrumbItems}
                                        />
                                    </div>

                                    <div className="breadcrumb-action">

                                        {currentStep >= VerificationStep.Tier && currentHistory?.status === "draft_verification" && (
                                            <Button
                                                className="breadcrumb-action abandon-btn"
                                                onClick={handleCancelFlow}
                                            >
                                                {t("common.cancel")}
                                            </Button>
                                        )}

                                        {currentStep === VerificationStep.Document &&
                                            currentHistory?.status === VerificationStatus.PENDING && (
                                                <Button
                                                    danger
                                                    className="breadcrumb-action"
                                                    onClick={handleResubmitVerification}
                                                >
                                                    {t("accountVerification.resubmitDocument")}
                                                </Button>
                                            )
                                        }

                                    </div>

                                </div>
                            )
                        }

                        {
                            currentStep === VerificationStep.Country
                            &&
                            (
                                <SelectCountry
                                    selectedCountry={selectedCountry}
                                    setSelectedCountry={setSelectedCountry}
                                    userVerificationProfile={userVerificationProfile}
                                    goNext={goNext}
                                    getUserVerificationFlows={getUserVerificationFlows}
                                />
                            )
                        }

                        {
                            currentStep === VerificationStep.Flow
                            &&
                            (
                                <SelectFlow
                                    userVerificationFlows={userVerificationFlows}
                                    selectedVerificationType={selectedVerificationType}
                                    handleSelectVerificationType={handleSelectVerificationType}
                                    handleStartVerification={handleStartVerification}
                                    goBack={goBack}
                                />
                            )
                        }

                        {
                            currentStep === VerificationStep.Tier
                            &&
                            (
                                <SelectTier
                                    userVerificationHistories={userVerificationHistories}
                                    userVerificationTiers={userVerificationTiers}
                                    refetchUserVerificationProfile={refetchUserVerificationProfile}
                                    goTo={goTo}
                                    setSelectedTier={setSelectedTier}
                                    accessRightsListCategory={accessRightsList}
                                />
                            )
                        }

                        {
                            (currentStep === VerificationStep.Document || currentStep === VerificationStep.DocumentGroup) &&
                            (
                                <SelectDocument
                                    currentHistory={currentHistory}
                                    setSelectedDocument={setSelectedDocument}
                                    setSelectedInnerDocument={setSelectedInnerDocument}
                                    selectedTier={selectedTier}
                                    selectedDocument={selectedDocument}
                                    userVerificationHistories={userVerificationHistories}
                                    goNext={goNext}
                                    goBack={goBack}
                                    goTo={goTo}
                                    isInnerDocument={currentStep === VerificationStep.DocumentGroup}
                                    tierSettingNextTierExist={tierSettingNextTierExist}
                                />
                            )
                        }

                        {
                            currentStep === VerificationStep.Form
                            &&
                            (
                                <DocumentForm
                                    currentHistory={currentHistory}
                                    selectedTier={selectedTier}
                                    selectedDocument={selectedDocument}
                                    selectedInnerDocument={selectedInnerDocument}
                                    goBack={goBack}
                                    goTo={goTo}
                                    refetchUserVerificationHistories={refetchUserVerificationHistories}
                                />
                            )
                        }

                        {
                            currentStep === VerificationStep.Status &&
                            <VerificationPending/>
                        }
                    </Space>
                </div>
            </Row>

            <Modal
                title={"Confirm Cancel"}
                cancelText={t("No, Continue")}
                centered={true}
                open={cancelOpen}
                getContainer={() => document.getElementById("layout-2")}
                width={isMobile ? "90%" : "350px"}
                okButtonProps={{className: "secondary_btn"}}
                onOk={async () => {
                    await apiCancelVerificationHistory(currentHistory?._id);
                    await refetchUserVerificationProfile();
                    await refetchUserVerificationHistories();
                    setCancelOpen(false)

                    setSelectedTier(null)
                    setSelectedDocument(null)
                    setSelectedDocument(null)
                    goTo(VerificationStep.Country)
                }}
                okText={"Yes, Cancel"}
                cancelButtonProps={{className: "primary_btn"}}
                onCancel={() => setCancelOpen(false)}
            >
                Are you sure you want to cancel this verification process? Your progress will be lost.
            </Modal>
        </div>
    )
}

function SelectCountry(props) {
    const {selectedCountry, setSelectedCountry, userVerificationProfile, goNext, getUserVerificationFlows} = props

    const {t} = useTranslation();
    const {assignUserVerificationProfile} = useAssignUserVerificationProfile();
    const isMobile = useIsMobile();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const openModal = () => setIsModalOpen(true);
    const closeModal = () => setIsModalOpen(false);

    const handleSelectCountry = () => {
        if (userVerificationProfile?.country && selectedCountry !== userVerificationProfile?.country) {
            openModal()
        } else {
            goNext()
        }
    }

    const handleChangeCountryFlow = async () => {
        await getUserVerificationFlows({country: selectedCountry});
        goNext()
    }

    return (
        <>
            <Card bordered={false} className={"shadow-card"}>
                <Space style={{width: "100%"}} size={16} direction="vertical">
                    <div className={"mb-4"}>
                        <h6><strong>{t("accountVerification.selectCountry")}</strong></h6>
                        <p className={"secondary-text"}>{t("accountVerification.note.selectCountry")}</p>
                    </div>

                    <Row gutter={16} align={"middle"}>
                        <Col flex={1}>
                            <Select
                                style={{width: "100%"}}
                                showSearch
                                placeholder={t("accountVerification.placeholder.selectCountry")}
                                optionFilterProp={"label"}
                                value={selectedCountry}
                                onChange={(country) => setSelectedCountry(country)}
                                suffixIcon={<CaretDownFilled style={{pointerEvents: "none"}}/>}
                                options={countryList_language?.map(d => ({
                                    ...d,
                                    label: t(d?.label),
                                }))}
                            />
                        </Col>
                        <Col span={24}>
                            <Row justify={"end"}>
                                <Button
                                    block={!!isMobile}
                                    disabled={!selectedCountry}
                                    onClick={handleSelectCountry}
                                    className={"mt-4 primary_btn"}
                                >
                                    {t("common.next")}
                                </Button>
                            </Row>
                        </Col>
                    </Row>
                </Space>
            </Card>

            <Modal
                className="verification-modal"
                title={t("Change Country")}
                open={isModalOpen}
                onCancel={closeModal}
                destroyOnClose
                centered
                getContainer="#layout-2"
                footer={
                    <Row justify={"end"} gutter={16}>
                        <Col>
                            <Button className={"secondary_btn"} onClick={closeModal}>{t("common.cancel")}</Button>
                        </Col>
                        <Col>
                            <Button className={"primary_btn"} onClick={handleChangeCountryFlow}>
                                {t("common.confirm")}
                            </Button>
                        </Col>
                    </Row>
                }
            >
                <div>{t("The selected country does not match your profile. Do you wish to continue?")}</div>
            </Modal>
        </>
    );
}

function SelectFlow(props) {
    const {
        userVerificationFlows,
        selectedVerificationType,
        handleSelectVerificationType,
        handleStartVerification,
        goBack
    } = props

    const {t} = useTranslation();
    const colorState = useRecoilValue(globalColorThemeState);
    const {assignUserVerificationProfile} = useAssignUserVerificationProfile();
    const {refetchUserVerificationHistories, refetchUserVerificationProfile} = useRecoilValue(verificationRefetchState)

    const [isModalOpen, setIsModalOpen] = useState(false);
    const openModal = () => setIsModalOpen(true);
    const closeModal = () => setIsModalOpen(false);

    const confirmStartVerification = async () => {
        try {
            if (selectedVerificationType) {
                const selectedFlow = userVerificationFlows?.find(flow => flow.verificationType === selectedVerificationType);


                if (!selectedFlow._id) {
                    return;
                }

                const result = await assignUserVerificationProfile(selectedFlow._id);

                if (result?.status === "Success") {
                    handleStartVerification(selectedVerificationType);
                }
            }
        } catch (error) {
            console.error("failed to start verification: ", error)
        } finally {
            closeModal();
        }
    };

    return (
        <>
            <Card bordered={false} className={"shadow-card"}>
                <Space style={{width: "100%"}} size={16} direction="vertical">
                    <h6 className="heading">
                        {t("accountVerification.note.selectVerificationType")}
                    </h6>

                    {[...(userVerificationFlows || [])]
                        .sort((a, b) => (a.verificationType === "individual" ? -1 : b.verificationType === "individual" ? 1 : 0))
                        .map((flow) => {
                            const displayName = extractLocalizedValue(flow.displayName) || flow.name;
                            const description = extractLocalizedValue(flow.description);
                            // const logo = flow?.[colorState]?.trim() || "";

                            const isSelected = selectedVerificationType === flow.verificationType;

                            return (
                                <Card
                                    key={flow._id}
                                    onClick={() => handleSelectVerificationType(flow.verificationType)}
                                    className={`account-verification-card-selection verification-card ${isSelected ? "selected" : ""}`}
                                    bodyStyle={{padding: "16px"}}
                                >
                                    <div className={"selection-content-box"}>
                                        <div className={"mt-1 ms-1 md:mt-0 md:ms-0"}
                                             style={{flex: 1, textAlign: "left"}}>
                                            <Row justify={"space-between"}>
                                                <div className="account-verification-card-selection-title">
                                                    {displayName}
                                                </div>
                                                <Tag className="verification-card-type align-self-start mt-1"
                                                     color={flow.verificationType === "individual" ? "geekblue" : "lime"}>
                                                    {t(`user.statusTag.${flow.verificationType}`)}
                                                </Tag>
                                            </Row>

                                            {
                                                description &&
                                                <p className="account-verification-card-selection-description">
                                                    {description}
                                                </p>
                                            }
                                        </div>
                                    </div>
                                </Card>
                            );
                        })}

                    <Row gutter={8} className={"mt-6"}>
                        <Col span={12}>
                            <Button className={"secondary_btn"} block onClick={goBack}>
                                {t("common.back")}
                            </Button>
                        </Col>
                        <Col span={12}>
                            <Button
                                block
                                className={"primary_btn"}
                                disabled={!selectedVerificationType}
                                onClick={openModal}
                            >
                                {t("accountVerification.startVerification")}
                            </Button>
                        </Col>
                    </Row>
                </Space>
            </Card>

            <Modal
                className="verification-modal"
                title={t("accountVerification.startVerification")}
                open={isModalOpen}
                onCancel={closeModal}
                destroyOnClose
                centered
                footer={
                    <Row justify={"end"} gutter={16}>
                        <Col>
                            <Button className={"secondary_btn"} onClick={closeModal}>{t("common.cancel")}</Button>
                        </Col>
                        <Col>
                            <Button className={"primary_btn"} onClick={confirmStartVerification}>
                                {t("accountVerification.startVerificationProcess")}
                            </Button>
                        </Col>
                    </Row>
                }
            >
                <div>{t("accountVerification.startVerificationDescription")}</div>
            </Modal>
        </>
    );
}

function SelectTier(props) {
    const {
        userVerificationHistories,
        goTo,
        setSelectedTier,
        userVerificationTiers,
        accessRightsListCategory
    } = props

    const {t} = useTranslation();
    const {initializeUserVerificationHistory} = useInitializeUserVerificationHistory();
    const {refetchUserVerificationProfile, refetchUserVerificationHistories} = useRecoilValue(verificationRefetchState);
    const isMobile = useIsMobile()
    const [recordExistModalOpen, setIsRecordExistModalOpen] = useState(null);

    const handleSelectTier = async (tier, startNewRecord = false) => {
        setSelectedTier(tier);
        const existingHistory = userVerificationHistories?.find(history => history.tierSetting === tier._id)
        const recordExist = ["canceled", "verification_failed"].includes(existingHistory?.status)

        if (!existingHistory?._id || (recordExist && startNewRecord)) {
            try {
                const result = await initializeUserVerificationHistory(tier._id);


                if (result?.status === "Success") {

                    await Promise.all([
                        refetchUserVerificationProfile(),
                        refetchUserVerificationHistories()
                    ]);

                    if (startNewRecord) return

                    const timeout = setTimeout(() => {
                        goTo(VerificationStep.Document);
                    }, 150);

                    setIsRecordExistModalOpen(null)
                    return () => clearTimeout(timeout);
                }

            } catch (error) {
                console.error("error initializing verification history:", error);
            }
        } else if (recordExist) {
            try {
                const result = await apiDuplicateUserVerificationHistory({_id: existingHistory?._id})

                if (result?.status === "Success") {
                    await Promise.all([
                        refetchUserVerificationProfile(),
                        refetchUserVerificationHistories()
                    ]);

                    const timeout = setTimeout(() => {
                        goTo(VerificationStep.Document);
                    }, 150);

                    setIsRecordExistModalOpen(null)
                    return () => clearTimeout(timeout);
                }
            } catch (e) {
                console.error("error duplicating verification history:", e);
            }
        } else {
            goTo(VerificationStep.Document);
        }
    }

    const getTierStatus = (tierId) => {
        const history = userVerificationHistories?.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))?.find((h) => h.tierSetting === tierId);
        return history?.status ?? null;
    }

    const getNextTier = (history, tiers) => {
        const validHistory = history.find(h => h.status === "pending_verification" || h.status === "verified")
        if (!validHistory) return tiers.find(t => t.tierLevel === 1)

        let nextTierLevel = validHistory.tierLevel + 1

        while (true) {
            const nextTier = tiers.find(t => t.tierLevel === nextTierLevel)
            if (!nextTier) return null

            const nextTierHistory = history.find(h => h.tierLevel === nextTierLevel)

            if (
                !nextTierHistory ||
                (nextTierHistory.status !== "pending_verification" && nextTierHistory.status !== "verified")
            ) {
                return nextTier
            }

            nextTierLevel++
        }
    }

    const currentTierAvailable = getNextTier(userVerificationHistories, userVerificationTiers)
    const currentTierAvailableId = currentTierAvailable?._id
    const currentTierLevel = currentTierAvailable?.tierLevel || 0

    const standaloneTier = userVerificationTiers?.find(d => d?.type === "standalone")

    const mapCategoryAccess = (category, access) => {
        const result = {}

        for (const [catKey, catValues] of Object.entries(category || {})) {
            const arr = []

            for (const [accKey, accVal] of access) {
                if (catValues[accKey]) {
                    arr.push([accKey, accVal])
                }
            }

            if (arr.length > 0) {
                result[catKey] = arr
            }
        }

        return result
    }

    return (
        <Row gutter={[16, 16]}>
            {(standaloneTier ? [standaloneTier] : userVerificationTiers)?.map((tier) => {
                const displayName = extractLocalizedValue(tier.displayName) || tier.name;
                const description = extractLocalizedValue(tier.description);
                const status = getTierStatus(tier._id);
                const tierVerificationHistory = userVerificationHistories.find(history => history.tierSetting === tier._id)
                const displayStatus = status && tierVerificationHistory?.documents !== null // user has at least submitted once before
                const documents = tier?.documentSettings?.map(doc => extractLocalizedValue(doc?.documentSettingDoc?.displayName) || doc?.documentSettingDoc?.name)

                const accessRightsList = Object.entries(tier?.accessRights || {}) || []
                const access = accessRightsList.filter(item => item[1] === null)
                const accessWithAmounts = accessRightsList.filter(item => item[1] !== null)
                const nextButtonAvailable = (!currentTierAvailableId && (tier?.tierLevel <= 1 || tier?.type === "standalone")) || tier?._id === currentTierAvailableId
                const accessWithCategory = mapCategoryAccess(accessRightsListCategory, access);

                return (
                    <Col className={"mh-100"} span={24} key={tier._id}>
                        <Tooltip placement="top">
                            <Card
                                className={`account-verification-card-selection h-full ${nextButtonAvailable ? "cursor-pointer" : ""}`}
                                onClick={() => {
                                    if (nextButtonAvailable && ["canceled", "verification_failed"].includes(tierVerificationHistory?.status)) {
                                        setIsRecordExistModalOpen(tier)
                                        return
                                    }
                                    if (nextButtonAvailable) handleSelectTier(tier)
                                }}
                            >
                                <Space direction="vertical" size={8} style={{width: "100%", height: "100%"}}>
                                    {
                                        status && isMobile ?
                                            <Tag color={getStatusColor(status)} bordered={false}>
                                                {t(`accountVerification.status.${status}`) || status.replace(/_/g, " ")}
                                            </Tag> : <></>
                                    }
                                    {
                                        <Row justify={(status && !isMobile) ? "space-between" : "start"}
                                             align={"middle"}>
                                            <div className="account-verification-card-selection-title">
                                                {displayName} {tier?.tierLevel ?
                                                <Tag className={"ms-1"}
                                                     color={"geekblue"}>{t("Tier")} {tier?.tierLevel}</Tag> : ""}
                                            </div>
                                            {
                                                status && !isMobile ?
                                                    <Tag color={getStatusColor(status)} bordered={false}>
                                                        {t(`accountVerification.status.${status}`) || status.replace(/_/g, " ")}
                                                    </Tag> : <></>
                                            }
                                        </Row>
                                    }
                                    <p>
                                        <small>{description}</small>
                                    </p>

                                    <Row gutter={4}>
                                        <Col span={isMobile ? 24 : 12}>
                                            <p>Docs Required</p>
                                            <Space direction={"vertical"} wrap>
                                                {documents?.map(d => (
                                                    <div className={"documents-tag"} key={d}>{d}</div>
                                                ))}
                                            </Space>

                                            {
                                                isMobile && <Divider className={"my-2"}/>
                                            }
                                        </Col>
                                        <Col span={isMobile ? 24 : 12}>
                                            <p>Access</p>
                                            {
                                                accessWithAmounts?.filter(([_, {dailyMax, allTimeMax}]) => dailyMax > 0 || allTimeMax > 0)?.length > 0 && (
                                                    <>
                                                        {
                                                            accessWithAmounts?.map(
                                                                ([module, {dailyMax, allTimeMax}], i) => {
                                                                    return (
                                                                        (dailyMax > 0 || allTimeMax > 0) &&
                                                                        <div key={module}>
                                                                            <div
                                                                                className={"documents-tag"}>{t(`accountVerification.tierSettingAccessRight.${module}`)}</div>
                                                                            <div className={"documents-tag-value mb-1"}>
                                                                                {dailyMax > 0 ? `Daily Max ${dailyMax || "0"} USD` : ""}
                                                                                {dailyMax > 0 && allTimeMax > 0 && " | "}
                                                                                {allTimeMax > 0 ? `All Time Max ${allTimeMax || "0"} USD` : ""}
                                                                            </div>
                                                                        </div>

                                                                    )
                                                                }
                                                            )
                                                        }
                                                        <Divider className={"my-2"}/>
                                                    </>
                                                )
                                            }
                                            <Collapse className={"access-rights-collapse"} ghost expandIcon={null}>
                                                {
                                                    Object.keys(accessWithCategory)?.map((cat) => (
                                                        <CollapsePanel
                                                            header={
                                                                <p>
                                                                    <small>{t(`accountVerification.tierSettingAccessRight.${cat?.replace(" ", "-")}`)}</small>
                                                                </p>
                                                            }
                                                            key={cat}
                                                        >
                                                            {
                                                                accessWithCategory[cat]?.map(
                                                                    ([module, value], i) => {
                                                                        return (
                                                                            <div className={"documents-tag"}
                                                                                 key={module}>
                                                                                {t(`accountVerification.tierSettingAccessRight.${module}`)}
                                                                            </div>
                                                                        )
                                                                    }
                                                                )
                                                            }
                                                        </CollapsePanel>
                                                    ))
                                                }
                                            </Collapse>
                                        </Col>
                                    </Row>
                                </Space>
                            </Card>
                        </Tooltip>
                    </Col>
                );
            })}

            <Modal
                open={!!recordExistModalOpen}
                getContainer={() => document.getElementById("layout-2")}
                className="verification-modal"
                title={t("Record existed, how do you with to continue?")}
                onCancel={() => setIsRecordExistModalOpen(null)}
                destroyOnClose
                width={"200px"}
                centered
                footer={
                    <Row justify={"end"} gutter={16}>
                        <Col>
                            <Button className={"secondary_btn"} onClick={() => setIsRecordExistModalOpen(null)}>{t("common.cancel")}</Button>
                        </Col>
                        <Col>
                            <Button className={"primary_btn"} onClick={() => handleSelectTier(recordExistModalOpen)}>
                                {t("Continue with last record")}
                            </Button>
                        </Col>
                        <Col>
                            <Button className={"primary_btn"} onClick={() => handleSelectTier(recordExistModalOpen, true)}>
                                Start New Record
                            </Button>
                        </Col>
                    </Row>
                }
            >

            </Modal>
        </Row>
    )
}

function SelectDocument(props) {
    const {
        currentHistory,
        selectedTier,
        setSelectedDocument,
        setSelectedInnerDocument,
        selectedDocument,
        isInnerDocument,
        goNext,
        goBack,
        goTo,
        userVerificationHistories,
        tierSettingNextTierExist
    } = props

    const isMobile = useIsMobile()
    const {t} = useTranslation();
    const setNotificationMessage = useSetRecoilState(globalNotificationState)

    const {submitUserVerificationHistory} = useSubmitUserVerificationHistory();
    const {refetchUserVerificationHistories} = useRecoilValue(verificationRefetchState);

    const isPendingVerification = currentHistory?.status === "pending_verification"

    const tierDocuments = isInnerDocument ? selectedDocument?.steps?.map(setting => ({
        ...setting.sourceDoc,
        isRequired: setting.isRequired,
    })) : selectedTier?.documentSettings?.map(
        setting => ({
            ...setting.documentSettingDoc,
            isRequired: setting.isRequired,
        })
    ) || [];

    const handleSelectDocument = (document) => {
        if (isInnerDocument) {
            setSelectedInnerDocument(document)
            goNext()
        } else {
            setSelectedInnerDocument(null)
            setSelectedDocument(document)
            goNext()

            if (document?.steps?.[0]?.sourceType === "field_setting") {
                goNext()
            }
        }
    }

    const handleSubmitVerification = async () => {
        if (!currentHistory?._id) return;

        if (selectedTier?.documentSettings) {
            for (const docSetting of selectedTier.documentSettings) {
                const docDef = docSetting.documentSettingDoc;
                if (!docDef?.steps) continue;

                const docHistory = currentHistory.documents?.find(d => d._id === docDef._id);

                for (const step of docDef.steps) {
                    if (step.isRequired && step.sourceDoc?.type === 'questionnaire') {
                        const stepHistory = docHistory?.steps?.find(s => s.sourceId === step.sourceDoc._id);
                        const recordId = stepHistory?.value;
                        const fieldName = extractLocalizedValue(step.sourceDoc.displayName) || step.sourceDoc.name;

                        if (!recordId) {
                            message.error(t("errorMsg.required.pleaseFillIn", {field: fieldName}));
                            return;
                        }

                        try {
                            const response = await apiGetQuestionnaireRecordStatus(recordId);
                            if (response?.status === 'success') {
                                const isSubmitted = response.data;
                                if (!isSubmitted) {
                                    message.error(`${t("Please submit the questionnaire first")}`);
                                    return;
                                }
                            }
                        } catch (error) {
                            console.error("Error checking questionnaire status", error);
                        }
                    }
                }
            }
        }

        try {
            const input = {
                _id: currentHistory._id,
                documents: currentHistory.documents?.map(doc => ({
                    ...doc,
                    steps: doc.steps?.map(cleanStep)
                }))
            }

            const result = await submitUserVerificationHistory(input)

            if (result?.status === "Success") {
                await refetchUserVerificationHistories();

                setNotificationMessage({
                    status: "success",
                    title: "",
                    msg: t("accountVerification.submitSuccessfully"),
                    key: "accountVerificationNotification",
                });

                if (tierSettingNextTierExist) {
                    goTo(VerificationStep.Tier)
                } else {
                    goTo(VerificationStep.Status)
                }
            }
        } catch (error) {
            console.error("handleSubmitTier: ", error)
            message.error(t("accountVerification.systemError"))
        }
    }

    return (
        <Row gutter={[16, 16]}>
            {tierDocuments?.map((document) => {
                const displayName = extractLocalizedValue(document.displayName) || document.name;
                const description = extractLocalizedValue(document.description);

                const tierHistory = userVerificationHistories?.find(
                    (h) => h.tierSetting === selectedTier._id
                );

                const submittedDoc = tierHistory?.documents?.find(
                    (d) => d._id === document._id
                );

                const isSubmittedInnerDoc = isInnerDocument ? tierHistory?.documents?.find(
                    (d) => d._id === selectedDocument?._id
                )?.steps?.find(d => d?.sourceId === document._id)?.value : null

                const status = (submittedDoc || isSubmittedInnerDoc) ? tierHistory.status : null;
                const color = status ? getStatusColor(status) : undefined

                return (
                    <Col span={24} key={document._id} className={"mh-100"}>
                        <Card className={`account-verification-card-selection h-full`}
                              onClick={() => handleSelectDocument(document)}>
                            {
                                (status && isMobile) &&
                                <div><Tag color={color}
                                          bordered={false}>{convertToUppercase(status.replace(/_/g, " "))}</Tag></div>
                            }
                            <div className="account-verification-card-selection-title">
                                {
                                    document.isRequired &&
                                    (
                                        <Tooltip title={"This section is required"}>
                                            <span className="account-verification-required-mark">
                                                *
                                            </span>
                                        </Tooltip>
                                    )
                                }
                                {displayName}
                                {
                                    (status && !isMobile) &&
                                    <Tag className={"ms-2"} color={color}
                                         bordered={false}>{convertToUppercase(status.replace(/_/g, " "))}</Tag>
                                }
                            </div>

                            <p className={"mb-2"}><small>{description}</small></p>
                        </Card>
                    </Col>
                );
            })}

            <Row justify={"end"} className={"w-full mt-6"}>
                <Space style={{display: "flex", justifyContent: "flex-end"}}>
                    <Button onClick={goBack} className={"secondary_btn"}>
                        {t("common.back")}
                    </Button>

                    {
                        !isPendingVerification
                        &&
                        (
                            <Button
                                className={"primary_btn"}
                                onClick={handleSubmitVerification}
                                disabled={isPendingVerification}
                            >
                                {t("common.submit")}
                            </Button>
                        )
                    }
                </Space>
            </Row>
        </Row>
    )
}

function DocumentForm(props) {
    const {
        currentHistory,
        selectedTier,
        selectedDocument,
        selectedInnerDocument,
        goBack,
        goTo,
        refetchUserVerificationHistories
    } = props

    const {t} = useTranslation();
    const setNotificationMessage = useSetRecoilState(globalNotificationState)

    const {saveUserVerificationHistory, loading: saveLoading} = useSaveUserVerificationHistory();

    const [form] = Form.useForm();
    const [saveStatus, setSaveStatus] = useState("idle"); // for label
    const [isSaving, setIsSaving] = useState(false); // for button

    const isPendingVerification = currentHistory?.status === VerificationStatus.PENDING

    const fields = (selectedInnerDocument || selectedDocument)?.steps?.map((step) => ({
        ...step.sourceDoc,
        isRequired: step.isRequired,
        ...(step.sourceDoc?.type === 'questionnaire' && {formSettingId: step.questionnaireId})
    })) || [];

    const debouncedSave = useRef(
        debounce(async (payload) => {
            try {
                setIsSaving(true)
                await saveUserVerificationHistory(payload);
                setSaveStatus("saved");
            } catch {
                setSaveStatus("idle");
            } finally {
                setIsSaving(false)
                setTimeout(() => setSaveStatus("idle"), 2000);
                await refetchUserVerificationHistories();
            }
        }, 1500)
    ).current;

    const handleAutoSave = useCallback(async () => {
        if (!currentHistory?._id || !selectedDocument?._id) return;

        const formValues = form.getFieldsValue();
        let innerDocuments

        let updatedDoc = {
            _id: selectedDocument._id,
            steps: buildSteps(formValues, selectedDocument.steps),
        };

        if (selectedInnerDocument) {
            innerDocuments = {
                _id: selectedInnerDocument._id,
                steps: buildSteps(formValues, selectedInnerDocument.steps),
            }

            updatedDoc = {
                _id: selectedDocument._id,
                steps: updatedDoc.steps.map((step) => {
                    if (step.sourceId === innerDocuments._id) {
                        return {
                            ...step,
                            value: {steps: innerDocuments.steps}
                        }
                    }

                    const found = currentHistory.documents?.find((doc) => doc._id === selectedDocument._id) || []
                    const foundInnerDoc = found?.steps?.find(doc => doc?.sourceId === step?.sourceId)

                    const isDocEmpty = !(foundInnerDoc?.value?.steps?.filter(d => d?.value)?.length)

                    return isDocEmpty ? {sourceId: step.sourceId, value: null} : foundInnerDoc ? {
                        ...foundInnerDoc,
                        sourceType: step.sourceType
                    } : step
                })
            }
        }

        const mergedDocuments = [...(currentHistory.documents || [])];
        const index = mergedDocuments.findIndex((d) => d._id === updatedDoc._id);
        if (index >= 0) mergedDocuments[index] = updatedDoc;
        else mergedDocuments.push(updatedDoc);

        const payload = {
            _id: currentHistory._id,
            documents: mergedDocuments.map(doc => ({
                ...doc,
                steps: doc.steps?.map(cleanStep)
            }))
        };

        setIsSaving(true);
        setSaveStatus("saving");

        debouncedSave(payload);
    }, [currentHistory._id, currentHistory.documents, selectedDocument._id, selectedDocument.steps, form, debouncedSave]);

    const handleSaveDocument = async () => {

        try {
            if (!currentHistory?._id || !selectedDocument?._id) return;

            const formValues = form.getFieldsValue();
            let innerDocuments

            let updatedDoc = {
                _id: selectedDocument._id,
                steps: buildSteps(formValues, selectedDocument.steps),
            };

            if (selectedInnerDocument) {
                innerDocuments = {
                    _id: selectedInnerDocument._id,
                    steps: buildSteps(formValues, selectedInnerDocument.steps),
                }

                updatedDoc = {
                    _id: selectedDocument._id,
                    steps: updatedDoc.steps.map((step) => {
                        if (step.sourceId === innerDocuments._id) {
                            return {
                                ...step,
                                value: {steps: innerDocuments.steps}
                            }
                        }

                        const found = currentHistory.documents?.find((doc) => doc._id === selectedDocument._id) || []
                        const foundInnerDoc = found?.steps?.find(doc => doc?.sourceId === step?.sourceId)

                        const isDocEmpty = !(foundInnerDoc?.value?.steps?.filter(d => d?.value)?.length)

                        return isDocEmpty ? {sourceId: step.sourceId, value: null} : foundInnerDoc ? {
                            ...foundInnerDoc,
                            sourceType: step.sourceType
                        } : step
                    })
                }
            }

            const mergedDocuments = [...(currentHistory.documents || [])];
            const index = mergedDocuments.findIndex((d) => d._id === updatedDoc._id);
            if (index >= 0) mergedDocuments[index] = updatedDoc;
            else mergedDocuments.push(updatedDoc);

            const payload = {
                _id: currentHistory._id,
                documents: mergedDocuments.map(doc => ({
                    ...doc,
                    steps: doc.steps?.map(cleanStep)
                }))
            };

            const result = await saveUserVerificationHistory(payload);


            if (result?.status === "Success") {
                await refetchUserVerificationHistories();

                setNotificationMessage({
                    status: "success",
                    title: "",
                    msg: t("accountVerification.saveSuccessfully"),
                    key: "accountVerificationNotification",
                });

                goTo(selectedInnerDocument ? VerificationStep.DocumentGroup : VerificationStep.Document);
            }

        } catch (error) {
            console.error("failed to save document")
        }
    }

    useEffect(() => {
        if (!currentHistory) return;

        const currentDoc = currentHistory.documents?.find((doc) => doc._id === selectedDocument._id)
        if (selectedInnerDocument && currentDoc?.steps?.length > 0) {
            const found = currentDoc?.steps?.find(doc => doc?.sourceId === selectedInnerDocument?._id)
            const currentInnerDoc = {_id: found?.sourceId, steps: found?.value?.steps || []}

            const initialValues = initializeDocumentForm(currentInnerDoc, selectedInnerDocument)
            form.setFieldsValue(initialValues)

            return
        }

        const initialValues = initializeDocumentForm(currentDoc, selectedDocument)
        form.setFieldsValue(initialValues)
    }, [currentHistory, selectedDocument, form, selectedInnerDocument])

    return (
        <>
            {saveStatus !== "idle" && (
                <div
                    style={{
                        textAlign: "right",
                        color: saveStatus === "saving" ? "#888" : "green",
                        marginBottom: 8,
                        transition: "all 0.3s",
                    }}
                >
                    {saveStatus === "saving"
                        ? t("accountVerification.saving")
                        : t("accountVerification.saved")}
                </div>
            )}

            <Card>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSaveDocument}
                    onValuesChange={() => {
                        handleAutoSave();
                    }}
                >
                    {
                        renderFormFields(t, form, fields, currentHistory?.status, handleAutoSave, [], {layout: "layout_2"})
                    }

                    <Form.Item>
                        <Space style={{display: "flex", justifyContent: "flex-end"}}>
                            <Button
                                onClick={() => {
                                    goTo(selectedInnerDocument ? VerificationStep.DocumentGroup : VerificationStep.Document)
                                }}
                                disabled={isSaving}
                                className={"secondary_btn"}
                            >
                                {t("common.back")}
                            </Button>


                            {
                                !isPendingVerification
                                && (
                                    <Button
                                        className={"primary_btn"}
                                        htmlType="submit"
                                        loading={saveLoading}
                                        disabled={saveLoading}
                                    >
                                        {t("common.save")}
                                    </Button>
                                )
                            }

                        </Space>
                    </Form.Item>
                </Form>
            </Card>
        </>

    )
}

function VerificationPending() {

    return (
        <div className={"flex mt-6"}>
            <div className={"mx-auto text-center"} style={{maxWidth: 600}}>
                <Space direction={"vertical"} align={"center"}>
                    <FireOutlined style={{fontSize: 32}}/>
                    <div>
                        <h4>{"Your verification is currently under review."}</h4>
                        <p>We’ll notify you as soon as the process is complete.</p>
                    </div>
                </Space>
            </div>
        </div>
    )
}